import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { TelegramQR } from '../components/TelegramQR';

interface Stats {
  sessions?: { total_sessions: number; total_tokens: number; avg_heal_loops: number; successful: number; failed: number; total_users: number };
  top_users?: Array<{ username: string; session_count: number; tokens: number }>;
  model_stats?: Array<{ model: string; input_tokens: number; output_tokens: number; total_cost_usd: number; avg_cost_per_1m_input: number; avg_cost_per_1m_output: number }>;
}

const ENDPOINT_URL = (import.meta.env.VITE_API_BASE ?? window.location.origin) + '/v1';

export function DashboardPage() {
  const [stats, setStats] = useState<Stats>({});
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.getStats().catch(() => ({})),
      api.getProfile().catch(() => null)
    ]).then(([statsRes, userRes]) => {
      setStats(statsRes);
      if (userRes?.user) setUser(userRes.user);
    }).finally(() => setLoading(false));
  }, []);

  const s = stats.sessions;
  const isAdmin = user?.role === 'admin';

  const STAT_CARDS = [
    { 
      label: isAdmin ? 'Total Users' : 'Token Balance',   
      value: loading ? '…' : isAdmin ? formatNum(s?.total_users ?? 0) : (user ? formatNum(Number(user.tokens_balance)) : '0'), 
      icon: isAdmin ? '👥' : '💰', 
      color: isAdmin ? '#f43f5e' : '#34d399' 
    },
    { label: 'Total Sessions',  value: loading ? '…' : formatNum(s?.total_sessions ?? 0), icon: '💬', color: '#a78bfa' },
    { label: 'Tokens Generated',value: loading ? '…' : formatNum(s?.total_tokens ?? 0),   icon: '⚡', color: '#22d3ee' },
    { label: 'Success Rate',    value: loading ? '…' : s ? `${Math.round((s.successful / Math.max(s.total_sessions, 1)) * 100)}%` : '0%', icon: '✅', color: '#10b981' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Erix Life Enterprise OS</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid-2" style={{ gap: '16px' }}>
        {STAT_CARDS.map(c => <StatCard key={c.label} {...c} />)}
      </div>

      {/* Model Costs Report (Admin Only) */}
      {isAdmin && stats.model_stats && stats.model_stats.length > 0 && (
        <div className="card" style={{ overflowX: 'auto' }}>
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>📊</span> Model Usage & Cost Report (30 Days)
          </div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Model</th>
                <th style={{ textAlign: 'right' }}>Input Tokens</th>
                <th style={{ textAlign: 'right' }}>Output Tokens</th>
                <th style={{ textAlign: 'right' }}>Cost/1M In</th>
                <th style={{ textAlign: 'right' }}>Cost/1M Out</th>
                <th style={{ textAlign: 'right' }}>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {stats.model_stats.map((m, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{m.model}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{formatNum(m.input_tokens)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{formatNum(m.output_tokens)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>${m.avg_cost_per_1m_input.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--text-muted)' }}>${m.avg_cost_per_1m_output.toFixed(2)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#34d399' }}>${m.total_cost_usd.toFixed(4)}</td>
                </tr>
              ))}
              <tr style={{ background: 'var(--bg-elevated)', borderTop: '2px solid var(--border-color)' }}>
                <td style={{ fontWeight: 800, color: 'var(--text-primary)', padding: '12px 16px' }}>TỔNG CỘNG ĐÀI THỌ</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>{formatNum(stats.model_stats.reduce((acc, m) => acc + m.input_tokens, 0))}</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>{formatNum(stats.model_stats.reduce((acc, m) => acc + m.output_tokens, 0))}</td>
                <td></td>
                <td></td>
                <td style={{ textAlign: 'right', fontWeight: 800, color: '#10b981' }}>${stats.model_stats.reduce((acc, m) => acc + m.total_cost_usd, 0).toFixed(4)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Endpoint + Telegram */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Endpoint Info */}
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔗</span> OpenAI Endpoint
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { label: 'Base URL', value: ENDPOINT_URL, accent: true },
            ].map(r => (
              <div key={r.label} style={{ background: 'var(--bg-elevated)', borderRadius: '6px', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{r.label}</div>
                <code style={{ fontSize: '11px', color: r.accent ? 'var(--accent-primary)' : 'var(--text-secondary)', fontFamily: 'var(--font-mono)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{r.value}</code>
              </div>
            ))}
          </div>
        </div>

        {/* Telegram QR */}
        <TelegramQR />
      </div>

      {/* Top users */}
      {(stats.top_users?.length ?? 0) > 0 && (
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '14px' }}>👤 Top Users</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {stats.top_users!.map((u, i) => (
              <div key={u.username} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: '6px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', width: '16px' }}>#{i + 1}</span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 600 }}>{u.username}</span>
                <span className="badge badge--blue">{u.session_count} sessions</span>
                <span className="badge badge--purple">{formatNum(u.tokens)} tok</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, color }: { label: string; value: string | number; icon: string; color: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value" style={{ color }}>{value}</div>
      <div className="stat-card__icon">{icon}</div>
    </div>
  );
}

function formatNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}
